import { useQuery } from '@tanstack/react-query';
import { pmSupabase } from '@/integrations/supabase/pmClient';
import { usePMAuth } from '@/contexts/PMAuthContext';

// Helper to invoke PM portal API edge function using the PM-scoped client
// so the correct AAL2 access token is sent in the Authorization header
export async function pmApi(action: string, params: Record<string, any> = {}) {
  const { data, error } = await pmSupabase.functions.invoke('pm-portal-api', {
    body: { action, ...params },
  });
  if (error) throw new Error(error.message || 'PM API error');
  if (data?.error) throw new Error(data.error);
  return data;
}

export function usePMDashboard(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-dashboard', activeProgramManagerId, programId],
    queryFn: () => pmApi('dashboard_metrics', { program_id: programId }),
    enabled: !!activeProgramManagerId,
    refetchInterval: 30000,
    staleTime: 15000,
  });
}

export function usePMCards(programId: string | null, filters?: Record<string, any>) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-cards', activeProgramManagerId, programId, filters],
    queryFn: async () => {
      let q = (pmSupabase as any).from('program_cards')
        .select('*, program_end_users(first_name, last_name, company_name, external_ref, email), card_programs(name, code), cards(card_id, last4, status, product_type, scheme, created_at, updated_at)')
        .eq('program_manager_id', activeProgramManagerId)
        .order('created_at', { ascending: false })
        .limit(200);
      if (programId) q = q.eq('program_id', programId);
      if (filters?.status) q = q.eq('status', filters.status);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
    enabled: !!activeProgramManagerId,
  });
}

export function usePMCardDetail(programCardId: string) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-card-detail', programCardId],
    queryFn: async () => {
      const { data, error } = await (pmSupabase as any).from('program_cards')
        .select('*, program_end_users(id, first_name, last_name, company_name, external_ref, email, status, kyc_status, risk_rating), card_programs(name, code, scheme, product_type), cards(card_id, last4, status, product_type, scheme, created_at, cardholder_name, expiry_month, expiry_year)')
        .eq('id', programCardId)
        .eq('program_manager_id', activeProgramManagerId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!activeProgramManagerId && !!programCardId,
  });
}

export function usePMTransactions(programId: string | null, filters?: Record<string, any>) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-transactions', activeProgramManagerId, programId, filters],
    queryFn: async () => {
      let pcQ = (pmSupabase as any).from('program_cards')
        .select('underlying_card_id, program_end_users(first_name, last_name)')
        .eq('program_manager_id', activeProgramManagerId);
      if (programId) pcQ = pcQ.eq('program_id', programId);
      const { data: pCards } = await pcQ;
      const cardIds = (pCards || []).filter((c: any) => c.underlying_card_id).map((c: any) => c.underlying_card_id);
      if (cardIds.length === 0) return [];

      const cardUserMap: Record<string, string> = {};
      for (const pc of pCards || []) {
        if (pc.underlying_card_id && pc.program_end_users) {
          cardUserMap[pc.underlying_card_id] = `${pc.program_end_users.first_name || ''} ${pc.program_end_users.last_name || ''}`.trim();
        }
      }

      let q = (pmSupabase as any).from('card_transactions')
        .select('card_txn_id, amount_minor, currency, created_at, status, type, mcc, merchant_country, merchant_name, decline_reason, card_id, cards(last4)')
        .in('card_id', cardIds)
        .order('created_at', { ascending: false })
        .limit(filters?.limit || 100);
      
      if (filters?.dateFrom) q = q.gte('created_at', filters.dateFrom);
      if (filters?.dateTo) q = q.lte('created_at', filters.dateTo);
      if (filters?.status) q = q.eq('status', filters.status);
      if (filters?.type) q = q.eq('type', filters.type);
      if (filters?.currency) q = q.eq('currency', filters.currency);
      if (filters?.mcc) q = q.eq('mcc', filters.mcc);

      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map((t: any) => ({
        ...t,
        last4: t.cards?.last4,
        end_user: cardUserMap[t.card_id] || '—',
      }));
    },
    enabled: !!activeProgramManagerId,
  });
}

export function usePMDisputes(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-disputes', activeProgramManagerId, programId],
    queryFn: async () => {
      let pcQ = (pmSupabase as any).from('program_cards')
        .select('underlying_card_id, program_end_users(first_name, last_name)')
        .eq('program_manager_id', activeProgramManagerId);
      if (programId) pcQ = pcQ.eq('program_id', programId);
      const { data: pCards } = await pcQ;
      const cardIds = (pCards || []).filter((c: any) => c.underlying_card_id).map((c: any) => c.underlying_card_id);
      if (cardIds.length === 0) return [];

      const cardUserMap: Record<string, string> = {};
      for (const pc of pCards || []) {
        if (pc.underlying_card_id && pc.program_end_users) {
          cardUserMap[pc.underlying_card_id] = `${pc.program_end_users.first_name || ''} ${pc.program_end_users.last_name || ''}`.trim();
        }
      }

      const { data, error } = await (pmSupabase as any).from('disputes')
        .select('*, cards(last4)')
        .in('card_id', cardIds)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data || []).map((d: any) => ({
        ...d,
        last4: d.cards?.last4,
        end_user: cardUserMap[d.card_id] || '—',
      }));
    },
    enabled: !!activeProgramManagerId,
  });
}

export function usePMEndUsers(programId: string | null, filters?: Record<string, any>) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-end-users', activeProgramManagerId, programId, filters],
    queryFn: async () => {
      const { data: progs } = await (pmSupabase as any).from('card_programs')
        .select('id').eq('program_manager_id', activeProgramManagerId);
      const progIds = programId ? [programId] : (progs || []).map((p: any) => p.id);
      if (progIds.length === 0) return [];

      let q = (pmSupabase as any).from('program_end_users')
        .select('*')
        .in('program_id', progIds)
        .order('created_at', { ascending: false })
        .limit(200);
      if (filters?.status) q = q.eq('status', filters.status);
      if (filters?.search) q = q.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,external_ref.ilike.%${filters.search}%`);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
    enabled: !!activeProgramManagerId,
  });
}

export function usePMAlerts(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-alerts', activeProgramManagerId, programId],
    queryFn: () => pmApi('list_alerts', { program_id: programId }),
    enabled: !!activeProgramManagerId,
  });
}

// ═══════════════════════════════════════════
// Phase 3 hooks
// ═══════════════════════════════════════════

export function usePMCompliance(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-compliance', activeProgramManagerId, programId],
    queryFn: () => pmApi('list_compliance_overview', { program_id: programId }),
    enabled: !!activeProgramManagerId,
  });
}

export function usePMReports(programId: string | null, reportType: string, dateFrom?: string, dateTo?: string) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-report', activeProgramManagerId, programId, reportType, dateFrom, dateTo],
    queryFn: () => pmApi('generate_report', { report_type: reportType, program_id: programId, date_from: dateFrom, date_to: dateTo }),
    enabled: !!activeProgramManagerId && !!reportType,
  });
}

export function usePMOrders(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-orders', activeProgramManagerId, programId],
    queryFn: () => pmApi('list_orders', { program_id: programId }),
    enabled: !!activeProgramManagerId,
  });
}

export function usePMControlProfiles(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-control-profiles', activeProgramManagerId, programId],
    queryFn: () => pmApi('list_control_profiles', { program_id: programId }),
    enabled: !!activeProgramManagerId,
  });
}

export function usePMApiCredentials(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-api-credentials', activeProgramManagerId, programId],
    queryFn: () => pmApi('list_api_credentials', { program_id: programId }),
    enabled: !!activeProgramManagerId,
  });
}

export function usePMBilling(programId: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-billing', activeProgramManagerId, programId],
    queryFn: () => pmApi('list_billing_overview', { program_id: programId }),
    enabled: !!activeProgramManagerId,
  });
}

export function usePMTeam() {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-team', activeProgramManagerId],
    queryFn: () => pmApi('list_team_members'),
    enabled: !!activeProgramManagerId,
  });
}

export function usePMAuditLog(filters?: Record<string, any>) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-audit-log', activeProgramManagerId, filters],
    queryFn: () => pmApi('list_audit_log', filters || {}),
    enabled: !!activeProgramManagerId,
  });
}

export function usePMSettings() {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-settings', activeProgramManagerId],
    queryFn: () => pmApi('get_settings'),
    enabled: !!activeProgramManagerId,
  });
}

// Phase 4B: PM Approvals Queue
export function usePMApprovals(statusFilter?: string) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-approvals', activeProgramManagerId, statusFilter],
    queryFn: () => pmApi('list_pm_approvals', { status: statusFilter }),
    enabled: !!activeProgramManagerId,
    refetchInterval: 15000,
  });
}

// Phase 5A: PM Branding View
export function usePMBranding(programId?: string | null) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-branding', activeProgramManagerId, programId],
    queryFn: async () => {
      let q = (pmSupabase as any)
        .from('program_branding')
        .select('*, card_programs(name, code)')
        .eq('program_manager_id', activeProgramManagerId)
        .order('updated_at', { ascending: false });
      if (programId) q = q.eq('program_id', programId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
    enabled: !!activeProgramManagerId,
    staleTime: 60000,
  });
}

// Phase 4B: PM Control Change Requests
export function usePMControlRequests(statusFilter?: string) {
  const { activeProgramManagerId } = usePMAuth();
  return useQuery({
    queryKey: ['pm-control-requests', activeProgramManagerId, statusFilter],
    queryFn: () => pmApi('list_control_requests', { status: statusFilter || 'all' }),
    enabled: !!activeProgramManagerId,
    staleTime: 15000,
    refetchInterval: 30000,
  });
}
